import { useMemo } from 'react';

import { session } from '@/shared/session/session';

import {
  DOCS_STORE_KEY,
  docsKeysKey,
  docsSelectedKeyKey,
  parseJson,
  readItem,
  writeItem,
} from '../api/docs-storage';
import { docsKeysSchema, type DocsKey } from '../schemas/docs-storage-schema';

import { useBrowserStorage } from './use-browser-storage';

export type { DocsKey } from '../schemas/docs-storage-schema';

const readKeys = (raw: string | null): DocsKey[] => docsKeysSchema.parse(parseJson(raw) ?? []);

const selectStore = (storeId: string) => writeItem('local', DOCS_STORE_KEY, storeId);

const selectKey = (storeId: string, prefix: string) =>
  writeItem('local', docsSelectedKeyKey(storeId), prefix);

/** Adds the key (replacing one with the same prefix) and selects it. */
function addKey(storeId: string, key: DocsKey) {
  const others = readKeys(readItem('local', docsKeysKey(storeId))).filter(
    (k) => k.prefix !== key.prefix,
  );
  writeItem('local', docsKeysKey(storeId), JSON.stringify([key, ...others]));
  selectKey(storeId, key.prefix);
}

function removeKey(storeId: string, prefix: string) {
  const rest = readKeys(readItem('local', docsKeysKey(storeId))).filter((k) => k.prefix !== prefix);
  writeItem('local', docsKeysKey(storeId), JSON.stringify(rest));
}

/**
 * The playground's keys (plan 9.1): the store the docs call, picked from the stores on `/me` and
 * remembered in `localStorage`, and the full keys this browser holds for it. The API never
 * returns a full key again, so only keys created or pasted here are listed. `selectedKey` is
 * what try-it, snippets and `<ApiKeyInline>` use; it falls back to the newest key.
 */
export function useDocsKeys() {
  const { me } = session.useSession();
  const stores = useMemo(() => me?.stores ?? [], [me]);
  const storedStoreId = useBrowserStorage('local', DOCS_STORE_KEY);
  const storeId = stores.some((s) => s.id === storedStoreId)
    ? storedStoreId
    : (stores[0]?.id ?? null);

  const rawKeys = useBrowserStorage('local', storeId ? docsKeysKey(storeId) : null);
  const keys = useMemo(() => readKeys(rawKeys), [rawKeys]);
  const selectedPrefix = useBrowserStorage('local', storeId ? docsSelectedKeyKey(storeId) : null);
  const selectedKey = keys.find((k) => k.prefix === selectedPrefix) ?? keys[0] ?? null;

  return {
    stores,
    storeId,
    store: stores.find((s) => s.id === storeId) ?? null,
    keys,
    selectedKey,
    selectStore,
    selectKey: (prefix: string) => {
      if (storeId) selectKey(storeId, prefix);
    },
    addKey: (key: DocsKey) => {
      if (storeId) addKey(storeId, key);
    },
    removeKey: (prefix: string) => {
      if (storeId) removeKey(storeId, prefix);
    },
  };
}
